/**
 * offlineStorage.js
 * Utility for caching saved properties and appointment details
 * for offline viewing via the service worker message API.
 */

const SW_TIMEOUT = 3000;

function sendToSW(message) {
  return new Promise((resolve) => {
    if (!navigator.serviceWorker?.controller) {
      resolve(null);
      return;
    }
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), SW_TIMEOUT);
    channel.port1.onmessage = (e) => {
      clearTimeout(timer);
      resolve(e.data);
    };
    navigator.serviceWorker.controller.postMessage(message, [channel.port2]);
  });
}

/**
 * Save data to offline cache via service worker.
 * @param {string} key  - cache key (e.g. 'wishlist', 'appointments')
 * @param {any}    data - serialisable data
 */
export async function saveOfflineData(key, data) {
  if (!('serviceWorker' in navigator)) return;
  try {
    navigator.serviceWorker?.controller?.postMessage({
      type: 'CACHE_OFFLINE_DATA',
      key,
      data,
    });
    // Also persist in localStorage as a fast synchronous fallback
    localStorage.setItem(`ks_offline_${key}`, JSON.stringify({ ts: Date.now(), data }));
  } catch (err) {
    console.warn('[offlineStorage] saveOfflineData error:', err);
  }
}

/**
 * Read data from offline cache.
 * Falls back to localStorage if SW is unavailable.
 * @param {string} key
 * @returns {Promise<any|null>}
 */
export async function getOfflineData(key) {
  if (!('serviceWorker' in navigator)) {
    return getLocalFallback(key);
  }
  try {
    const result = await sendToSW({ type: 'GET_OFFLINE_DATA', key });
    if (result?.data) return result?.data;
  } catch {
    // fall through to localStorage
  }
  return getLocalFallback(key);
}

function getLocalFallback(key) {
  try {
    const raw = localStorage.getItem(`ks_offline_${key}`);
    if (!raw) return null;
    const { data } = JSON.parse(raw);
    return data ?? null;
  } catch {
    return null;
  }
}

/**
 * Check if the app is currently offline.
 */
export function isOffline() {
  return !navigator.onLine;
}

/**
 * Register a listener for online/offline transitions.
 * Returns an unsubscribe function.
 */
export function onConnectivityChange(callback) {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}
